import {
  DownOutlined,
  HolderOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Checkbox, Tooltip, Typography, theme } from 'antd';
import type { CSSProperties } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useMapLayers,
  type MapLayerKey,
  type MapLayerState,
} from '@/stores/MapLayersContext';

const PANEL_WIDTH_PX = 240;
const COLLAPSED_WIDTH_PX = 140;
const COLOR_SWATCH_PX = 12;
const COLLAPSED_STORAGE_KEY = 'rt.mapLegend.collapsed';

/**
 * Marker colours shown in the legend — must stay in sync with the
 * layer paint definitions in `routesGeoJsonLayer.ts`, `barriersLayer.ts`,
 * and `detourWaypointsLayer.ts`.
 */
const LAYER_COLORS: Record<MapLayerKey, string> = {
  barriers: '#cf1322',
  detours: '#1677ff',
  routes_barrier_aware: '#389e0d',
  routes_gis: '#d4a017',
  routes_iteko: '#722ed1',
  // For the industrial reference layer we use the LOCATION colour as
  // the legend marker. The actual map renders three colours (LOCATION
  // blue / AGRO_POINT orange / TERMINAL purple) per feature `type`.
  industrial_ref_book: '#1677ff',
  // Hard-prohibition red for the freight-restrictions legend swatch;
  // the actual map renders one of nine colours per `kind` (see
  // freightRestrictionsLayer.ts).
  freight_restrictions: '#cf1322',
};

export default function MapLegend() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { layers, toggleVisibility, reorder } = useMapLayers();
  const [collapsed, setCollapsed] = useState<boolean>(_readCollapsed);

  const sensors = useSensors(
    // Require a small drag distance so checkbox clicks aren't swallowed.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const ids = useMemo(() => layers.map((l) => l.key), [layers]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id as MapLayerKey);
    const to = ids.indexOf(over.id as MapLayerKey);
    if (from < 0 || to < 0) return;
    reorder(from, to);
  };

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      _writeCollapsed(next);
      return next;
    });
  }, []);

  const panelStyle: CSSProperties = {
    position: 'absolute',
    top: 16,
    left: 16,
    width: collapsed ? COLLAPSED_WIDTH_PX : PANEL_WIDTH_PX,
    // Frosted-glass effect over the basemap. RGBA fill ~80% opacity
    // keeps tokens readable while letting the underlying map show
    // through; backdrop-filter blurs anything behind the panel.
    background: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    boxShadow: token.boxShadow,
    padding: collapsed
      ? `${token.paddingXS}px ${token.paddingSM}px`
      : `${token.paddingSM}px ${token.paddingSM}px ${token.paddingXS}px`,
    zIndex: 400,
    pointerEvents: 'auto',
    transition: 'width 160ms ease',
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    marginBottom: collapsed ? 0 : 6,
  };

  return (
    <div style={panelStyle} aria-label={t('mapLegend.title')}>
      <div style={headerStyle}>
        <Typography.Text strong style={{ fontSize: 12 }}>
          {t('mapLegend.title')}
        </Typography.Text>
        <Tooltip
          title={collapsed ? t('mapLegend.expand') : t('mapLegend.collapse')}
        >
          <Button
            type="text"
            size="small"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={
              collapsed ? t('mapLegend.expand') : t('mapLegend.collapse')
            }
            style={{ marginRight: -6 }}
          />
        </Tooltip>
      </div>
      {!collapsed && (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {layers.map((layer) => (
                <LegendRow
                  key={layer.key}
                  layer={layer}
                  onToggle={() => toggleVisibility(layer.key)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 10, display: 'block', marginTop: 4 }}
          >
            {t('mapLegend.reorderHint')}
          </Typography.Text>
        </>
      )}
    </div>
  );
}

function _readCollapsed(): boolean {
  // localStorage is fine for UI preference — no PII, no tokens.
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function _writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage quota / private mode: silently ignore; preference just
    // won't persist across reloads.
  }
}

interface LegendRowProps {
  readonly layer: MapLayerState;
  readonly onToggle: () => void;
}

function LegendRow({ layer, onToggle }: LegendRowProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.key });

  const rowStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 2px',
    background: isDragging ? token.colorFillTertiary : 'transparent',
    borderRadius: token.borderRadiusXS,
    opacity: layer.disabled ? 0.45 : 1,
  };

  const labelStyle: CSSProperties = {
    flex: 1,
    fontSize: 12,
    color: layer.disabled ? token.colorTextDisabled : token.colorText,
    userSelect: 'none',
  };

  return (
    <div ref={setNodeRef} style={rowStyle}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: layer.disabled ? 'not-allowed' : 'grab',
          color: token.colorTextTertiary,
          display: 'flex',
        }}
        aria-label={t('mapLegend.dragHandle')}
      >
        <HolderOutlined />
      </span>
      <ColorSwatch color={LAYER_COLORS[layer.key]} />
      <Checkbox
        checked={layer.visible && !layer.disabled}
        disabled={layer.disabled}
        onChange={onToggle}
        aria-label={t(`mapLegend.layer.${layer.key}`)}
      />
      <Tooltip title={layer.disabled ? t('mapLegend.layerDisabled') : undefined}>
        <span style={labelStyle}>{t(`mapLegend.layer.${layer.key}`)}</span>
      </Tooltip>
    </div>
  );
}

interface ColorSwatchProps {
  readonly color: string;
}

function ColorSwatch({ color }: ColorSwatchProps) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: COLOR_SWATCH_PX,
        height: COLOR_SWATCH_PX,
        background: color,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
  );
}
